import React, { useState, useEffect } from 'react'
import { FaCheck, FaCog, FaPaperPlane } from 'react-icons/fa'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { addPermission, showPermission } from '../../api/adminApi'
import Swal from 'sweetalert2'

const AddPermission = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [selectedPermissions, setSelectedPermissions] = useState([])
  const queryClient = useQueryClient()

  console.log('selectedPermissions:', selectedPermissions)

  // Mapping of features to their permission IDs
  const permissionMap = {
    1: { name: 'dashboard', label: 'Dashboard', category: 'Core' },
    2: { name: 'sliders', label: 'Sliders Management', category: 'Content' },
    3: { name: 'trendingNews', label: 'Trending News', category: 'Content' },
    4: { name: 'preseed', label: 'Pre-seed Stage', category: 'Deal' },
    5: { name: 'seed', label: 'Seed Stage', category: 'Deal' },
    6: { name: 'growth', label: 'Growth Stage', category: 'Deal' },
    7: { name: 'ma', label: 'M&A Deals', category: 'Deal' },
    8: { name: 'ipo', label: 'IPO Tracking', category: 'Deal' },
    9: { name: 'world', label: 'World Markets', category: 'Deal' },
    10: { name: 'latestDeal', label: 'Latest Deals', category: 'Insight' },
    11: { name: 'funding365', label: 'Funding 365', category: 'Insight' },
    12: {
      name: 'financial',
      label: 'Financial Reports',
      category: 'Insight',
    },
    13: {
      name: 'customiseReport',
      label: 'Custom Reports',
      category: 'Insight',
    },
    14: { name: 'sampleReport', label: 'Sample Reports', category: 'Reports' },
    15: { name: 'invoice', label: 'Invoice Management', category: 'Payment' },
    16: { name: 'paymentStatus', label: 'Payment Status', category: 'Payment' },
    17: {
      name: 'productDeliver',
      label: 'Product Delivery',
      category: 'Payment',
    },
    18: { name: 'users', label: 'User Management', category: 'User' },
    19: { name: 'userActivity', label: 'User Activity', category: 'User' },
    21: { name: 'userCart', label: 'User Cart', category: 'User' },
  }

  // Fetch current permissions
  const {
    data: currentPermissions = [],
    isLoading: isLoadingPermissions,
    error,
  } = useQuery({
    queryKey: ['permissions', id],
    queryFn: () => showPermission(id),
    enabled: !!id,
  })

  // Update selected permissions when data is fetched
  useEffect(() => {
    if (currentPermissions && Array.isArray(currentPermissions)) {
      // Convert permission names to IDs
      const permissionIds = currentPermissions
        .map((permissionName) => {
          const foundPermission = Object.entries(permissionMap).find(
            ([id, permission]) => permission.name === permissionName
          )
          return foundPermission ? parseInt(foundPermission[0]) : null
        })
        .filter((id) => id !== null)

      setSelectedPermissions(permissionIds)
    }
  }, [currentPermissions])

  // Mutation for updating permissions
  const updatePermissionsMutation = useMutation({
    mutationFn: (permissions) => addPermission(id, permissions),
    onSuccess: () => {
      toast.success('Permissions updated successfully!')
      queryClient.invalidateQueries(['permissions', id])
      navigate('/dealdashboard/admin')
    },
    onError: (error) => {
      toast.error(error.response.data.message)
      console.error('API Error:', error.response.data.message)
    },
  })

  // Group permissions by category
  const groupedPermissions = Object.entries(permissionMap).reduce(
    (acc, [permId, permission]) => {
      const category = permission.category
      if (!acc[category]) {
        acc[category] = []
      }
      acc[category].push({ id: parseInt(permId), ...permission })
      return acc
    },
    {}
  )

  const handlePermissionToggle = (permissionId) => {
    setSelectedPermissions((prev) => {
      if (prev.includes(permissionId)) {
        return prev.filter((permId) => permId !== permissionId)
      } else {
        return [...prev, permissionId]
      }
    })
  }

  const handleSelectAll = () => {
    const allIds = Object.keys(permissionMap).map((permId) => parseInt(permId))
    setSelectedPermissions(allIds)
  }

  const handleClearAll = () => {
    setSelectedPermissions([])
  }

  const handleApiCall = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You are going Update Permission',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
    })

    if (result.isConfirmed) {
      updatePermissionsMutation.mutate(selectedPermissions)
    }
  }

  const getCategoryIcon = (category) => {
    const icons = {
      Core: '🏠',
      Content: '📰',
      Deal: '🤝',
      Insight: '📈',
      Reports: '📋',
      Payment: '💳',
      User: '👥',
    }
    return icons[category] || '📌'
  }

  // Loading state
  if (isLoadingPermissions) {
    return (
      <div className="max-w-6xl mx-auto p-6 bg-white">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3">
            <div
              className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: '#FF7010' }}
            ></div>
            <span className="text-gray-600">Loading permissions...</span>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-6 bg-white">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-500 text-lg mb-2">
              ❌ Error loading permissions
            </div>
            <div className="text-gray-600">{error.message}</div>
            <button
              onClick={() => queryClient.invalidateQueries(['permissions', id])}
              className="mt-4 px-4 py-2 text-white rounded hover:opacity-90"
              style={{ backgroundColor: '#FF7010' }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-black mb-2 flex items-center gap-3">
          <FaCog style={{ color: '#FF7010' }} />
          Admin Permissions Manager
        </h1>
        <p className="text-gray-600">
          Configure user permissions for admin panel features (User ID: {id})
        </p>
      </div>

      {/* Summary and Actions */}
      <div
        style={{ backgroundColor: '#FFF4F0' }}
        className="rounded-lg p-4 mb-6"
      >
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="text-sm text-black">
              <span className="font-semibold">
                {selectedPermissions.length}
              </span>{' '}
              permissions selected
            </div>
            <div
              className="text-xs text-black font-mono px-2 py-1 rounded"
              style={{ backgroundColor: '#FFE5D9' }}
            >
              [{selectedPermissions.join(', ')}]
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSelectAll}
              className="px-3 py-1 text-sm text-white rounded hover:opacity-90 transition-all"
              style={{ backgroundColor: '#FF7010' }}
            >
              Select All
            </button>
            <button
              onClick={handleClearAll}
              className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
            >
              Clear All
            </button>
          </div>
        </div>
      </div>

      {/* Permissions Grid */}
      <div className="space-y-6 mb-8">
        {Object.entries(groupedPermissions).map(([category, permissions]) => (
          <div key={category} className="border rounded-lg overflow-hidden">
            <div className="bg-gray-100 px-4 py-3 border-b">
              <h3 className="font-semibold text-black flex items-center gap-2">
                <span>{getCategoryIcon(category)}</span>
                {category}
                <span className="text-sm text-gray-600 ml-2">
                  ({permissions.length} items)
                </span>
              </h3>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {permissions.map((permission) => (
                  <label
                    key={permission.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedPermissions.includes(permission.id)
                        ? 'bg-orange-50 hover:bg-orange-100'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    style={{
                      borderColor: selectedPermissions.includes(permission.id)
                        ? '#FF7010'
                        : undefined,
                      backgroundColor: selectedPermissions.includes(
                        permission.id
                      )
                        ? '#FFF4F0'
                        : undefined,
                    }}
                  >
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={selectedPermissions.includes(permission.id)}
                        onChange={() => handlePermissionToggle(permission.id)}
                        className="sr-only"
                      />
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          selectedPermissions.includes(permission.id)
                            ? 'border-gray-300'
                            : 'border-gray-300'
                        }`}
                        style={{
                          backgroundColor: selectedPermissions.includes(
                            permission.id
                          )
                            ? '#FF7010'
                            : 'transparent',
                          borderColor: selectedPermissions.includes(
                            permission.id
                          )
                            ? '#FF7010'
                            : '#d1d5db',
                        }}
                      >
                        {selectedPermissions.includes(permission.id) && (
                          <FaCheck className="w-3 h-3 text-white" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-black">
                        {permission.label}
                      </div>
                      <div className="text-xs text-gray-500 font-mono">
                        ID: {permission.id}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* API Call Section */}
      <div className="flex justify-between items-center">
        <Link
          to="/dealdashboard/admin"
          className="border px-3 py-2 rounded bg-gray-300"
        >
          Cancel
        </Link>
        <button
          onClick={handleApiCall}
          disabled={
            updatePermissionsMutation.isPending ||
            selectedPermissions.length === 0
          }
          className={`w-full md:w-auto px-6 py-3 font-semibold rounded-lg transition-all flex items-center justify-center gap-2 ${
            updatePermissionsMutation.isPending ||
            selectedPermissions.length === 0
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'text-white hover:opacity-90 hover:shadow-lg'
          }`}
          style={{
            backgroundColor:
              updatePermissionsMutation.isPending ||
              selectedPermissions.length === 0
                ? undefined
                : '#FF7010',
          }}
        >
          {updatePermissionsMutation.isPending ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Updating...
            </>
          ) : (
            <>
              <FaPaperPlane className="w-4 h-4" />
              Update Permissions
            </>
          )}
        </button>
      </div>
    </div>
  )
}

export default AddPermission
